/**
 * FilterChipsList Component
 *
 * Horizontally scrollable row of filter chips, optionally split into multiple
 * labeled groups rendered inline on one line.
 *
 * Layout per Figma (node 10569:74416): groups are separated by an 18px gap with
 * a 1px vertical divider between them. Each group lays out as `{label}` (Inter
 * Regular) with 12px gap before its chip cluster, and chips within the cluster
 * are spaced 6px apart.
 *
 * Empty groups are skipped. The whole row collapses with a height animation
 * when no group has chips.
 */

// React
import { Fragment } from 'react';

// Animation
import { AnimatePresence, motion } from 'framer-motion';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { useScrollFades } from '@hooks/useScrollFades';

// Local components
import { FilterChip } from './FilterChip';

export interface FilterChipItem {
  /** Stable key for AnimatePresence / React reconciliation. */
  id: string;
  /** Visible text inside the chip. */
  label: string;
  /** Called when the chip's X is clicked. */
  onDismiss: () => void;
  /** Custom aria-label for the dismiss button. */
  dismissAriaLabel?: string;
}

export interface FilterChipGroup {
  /** Label rendered before the group's chips (e.g. "Catalogue"). */
  label: string;
  /** Chips in this group. Empty groups are skipped. */
  chips: FilterChipItem[];
}

export interface FilterChipsListProps {
  groups: FilterChipGroup[];
  className?: string;
}

export function FilterChipsList({ groups, className }: FilterChipsListProps) {
  const nonEmptyGroups = groups.filter((g) => g.chips.length > 0);
  const totalChips = nonEmptyGroups.reduce((sum, g) => sum + g.chips.length, 0);
  const { scrollRef, showLeftFade, showRightFade } = useScrollFades([totalChips]);
  const hasChips = nonEmptyGroups.length > 0;

  return (
    <AnimatePresence initial={false}>
      {hasChips && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className={cn('overflow-hidden flex-shrink-0', className)}
        >
          <div className="relative">
            <div
              className={cn(
                'pointer-events-none absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-off-white to-transparent z-10 transition-opacity duration-200',
                showLeftFade ? 'opacity-100' : 'opacity-0',
              )}
            />
            <div
              className={cn(
                'pointer-events-none absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-off-white to-transparent z-10 transition-opacity duration-200',
                showRightFade ? 'opacity-100' : 'opacity-0',
              )}
            />
            <div
              ref={scrollRef}
              className={cn(
                'flex items-center gap-6 overflow-x-auto overflow-y-hidden',
                '[&::-webkit-scrollbar]:h-6',
                '[&::-webkit-scrollbar-track]:bg-off-white',
                '[&::-webkit-scrollbar-thumb]:bg-dinnerplate-white [&::-webkit-scrollbar-thumb]:rounded-full',
                '[&::-webkit-scrollbar-thumb]:border-[6px] [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-off-white',
              )}
            >
              {nonEmptyGroups.map((group, i) => (
                <Fragment key={group.label}>
                  {i > 0 && (
                    <div
                      aria-hidden="true"
                      className="h-[18px] w-px flex-shrink-0 bg-dinnerplate-white"
                    />
                  )}
                  <div className="flex flex-shrink-0 items-center gap-4">
                    <span className="flex-shrink-0 text-sm text-grey font-inter font-normal leading-[1.5] antialiased">
                      {group.label}
                    </span>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <AnimatePresence mode="popLayout" initial={false}>
                        {group.chips.map((chip) => (
                          <FilterChip
                            key={chip.id}
                            label={chip.label}
                            onDismiss={chip.onDismiss}
                            dismissAriaLabel={chip.dismissAriaLabel}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  </div>
                </Fragment>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
